/**
 * /reports/reimbursement — รายการตามเบิก, now three tabs over three UNCHANGED components.
 *
 * ⚠️ A THIN SHELL, NEVER A MERGED COMPONENT. React hooks cannot be conditional, so one
 * combined body would fire Reimbursement's api 639 aggregate AND its realtime subscription
 * AND ClaimImports' two fetches on every visit, whichever tab is showing. The three files
 * also collide at module scope — `Th`/`Td` are declared with different signatures in
 * ClaimImports.jsx and SsoImport.jsx, and there are three different `fmt`.
 *
 * ⚠️ UNMOUNT ON SWITCH, deliberately — not the keep-mounted/`display:none` pattern.
 * Keeping the panels alive looks cheaper and is not:
 *   · a hidden ledger keeps re-firing api 639 through Reimbursement.jsx's own
 *     `visibilitychange` handler, for a panel nobody can see;
 *   · a hidden ClaimImports still runs its effects and would call setSearchParams while the
 *     operator is on another tab — two setSearchParams in one tick and one write is dropped;
 *   · a hidden ClaimImports FREEZES the courier pulse, which renders as relative minutes and
 *     is, by its own comment, the only thing that tells a dead robot apart from a quiet one.
 *     Keep-mounted turns the one safeguard into a false all-clear;
 *   · a display:none panel has zero height, so scrollTop clamps to 0 and flipping back lands
 *     you at the top regardless;
 *   · and it STILL would not protect the ปกส. reconcile, because opening a run detail is a
 *     separate route that unmounts the hub anyway.
 * The state that must not die moved into `useEphemeralState` instead — in-memory, survives
 * SPA navigation and tab flips, resets on a hard reload. See SsoImport.jsx / Reimbursement.jsx.
 */
import { useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Landmark } from 'lucide-react'
import { Segmented } from '../statistic/statShared.jsx'
import { usePersistedState } from '../../lib/persist.js'
import Reimbursement from './Reimbursement.jsx'
import ClaimImports from './ClaimImports.jsx'
import SsoImport from './SsoImport.jsx'
import NhsoImport from './NhsoImport.jsx'
import FollowUpLetter from './FollowUpLetter.jsx'

/* ⚠️ `v` values are the URL contract — they are in bookmarks, in the KeepQuery redirects
   in App.jsx, and in the Reports tile. Labels are free to change; these strings are not. */
const TABS = [
  { v: 'ledger', label: 'ตรวจสอบ', Comp: Reimbursement },
  { v: 'insurer', label: 'นำเข้า ประกัน/Partner', Comp: ClaimImports },
  { v: 'sso', label: 'นำเข้าประกันสังคม', Comp: SsoImport },
  { v: 'nhso', label: 'นำเข้า สปสช.', Comp: NhsoImport },
  { v: 'followup', label: 'ออกเอกสารติดตาม', Comp: FollowUpLetter },
]

/* ⚠️ The insurer line carries the safety promise that used to live in ClaimImports' own
   subtitle. It must not be lost in the de-chroming — it is the sentence that tells an
   operator an upload changes nothing on its own. */
const SUBTITLE = {
  ledger: 'รายการที่เก็บเงินจากผู้จ่ายภายนอกแทนคนไข้ · สรุปยอดตามผู้จ่าย · ทำเครื่องหมายรับแล้ว · ส่งออก CSV/PDF',
  insurer: 'อัปโหลดไฟล์สรุปการจ่ายจากบริษัทประกัน ระบบจะจับคู่กับรายการตามเบิกในระบบและแสดงผลให้ตรวจก่อน — ยังไม่มีการบันทึกใด ๆ จนกว่าจะกดยืนยัน',
  sso: 'อัปโหลด PDF รายงานการส่งเบิกจาก mms.sso.go.th · เทียบกับรายการ ปกส. ในระบบ · เห็นทั้งที่จ่ายแล้วและที่เรารักษาแต่ยังไม่ได้เงิน',
  nhso: 'อัปโหลดไฟล์ Excel รายงานสรุปการขอเบิกชดเชยจาก สปสช. · เทียบกับรายการ สปสช. ในโปรแกรม · เห็นรายการที่ สปสช. ไม่จ่ายพร้อมเหตุผล',
  followup: 'สร้างเอกสาร PDF (A4) ส่งให้ผู้จ่าย เพื่อติดตามรายการที่ยังค้างรับ · อ่านอย่างเดียว ไม่มีการเปลี่ยนสถานะใด ๆ',
}

export default function ReimbursementHub() {
  const [params, setParams] = useSearchParams()
  const [remembered, setRemembered] = usePersistedState('reim:tab', 'ledger')

  const raw = params.get('tab')
  const tab = TABS.some((t) => t.v === raw) ? raw : remembered
  const active = TABS.find((t) => t.v === tab) || TABS[0]

  /* ⚠️ FUNCTIONAL FORM, in both places. `setParams({ tab: v })` — the object form used by
     Implant.jsx — REPLACES the whole query string, which would wipe every ประวัติ filter on
     a tab flip and again on the mount-sync below. */
  const setTab = (v) => {
    setRemembered(v)
    setParams((prev) => { const n = new URLSearchParams(prev); n.set('tab', v); return n }, { replace: true })
  }

  // Put the resolved tab in the URL when it arrived from memory, so the address bar is
  // always shareable and a reload lands where the operator is looking.
  useEffect(() => {
    if (raw !== active.v) {
      setParams((prev) => { const n = new URLSearchParams(prev); n.set('tab', active.v); return n }, { replace: true })
    }
  }, [raw, active.v]) // eslint-disable-line react-hooks/exhaustive-deps

  const Panel = active.Comp

  return (
    <div className="px-8 py-6 max-w-[1220px]">
      <div className="text-[12.5px] text-slate-400 flex items-center gap-1.5 mb-2">
        <Link to="/reports" className="hover:text-brand-600">รายงาน</Link><span>/</span>
        <span className="text-slate-600">รายการตามเบิก</span>
      </div>

      <div className="flex items-start gap-3 mb-1">
        <span className="mt-0.5 inline-flex items-center justify-center w-9 h-9 rounded-xl bg-brand-50 text-brand-600">
          <Landmark size={18} />
        </span>
        <div className="min-w-0">
          <h1 className="text-[22px] font-semibold text-slate-900 tracking-tight">รายการตามเบิก</h1>
          <p className="text-[13.5px] text-slate-500 mt-0.5">{SUBTITLE[active.v]}</p>
        </div>
      </div>

      <div className="mt-4 mb-5">
        <Segmented value={active.v} onChange={setTab} options={TABS.map(({ v, label }) => ({ v, label }))} />
      </div>

      <Panel />
    </div>
  )
}
